import { s3URL } from '@mathflat/common/index'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useEffect } from 'react'
import { matchPath, useLocation } from 'react-router'
import { RouteName } from 'src/router'

import useRepository from '@/hooks/useRepository'
import commonRepo from '@/repository/common.repo'
import StudentGroupAccordionService, {
  type ServiceNameT,
} from '@/service/StudentGroupAccordion.service.ts'
import type { RouteKey } from '@/types'
import navigationUtils from '@/utils/navigationUtils'

import { logoStyle } from './AcademyLogo.style'
import { getHomeButtonLandingPath } from './utils'

interface Props {
  routeKey: RouteKey
  className?: string
}

const AcademyLogo = ({ routeKey, className }: Props) => {
  const studentGroupAccordionService = useRepository(
    [StudentGroupAccordionService, routeKey],
    routeKey as ServiceNameT,
  )

  const location = useLocation()
  const [isVisibleLogo, setVisibleLogo] = React.useState(false)

  const isLessonPreparation = !!matchPath(location.pathname, {
    path: RouteName.lessonPreparation,
  })

  const handleClickLogo = async () => {
    const landingPathUrl = await getHomeButtonLandingPath()

    if (navigationUtils.isAbsoluteUrl(landingPathUrl)) {
      window.open(landingPathUrl, '_blank')
      return
    }
    commonRepo.router?.push(landingPathUrl)
  }

  useEffect(() => {
    checkVisibleLogo()
  }, [location.pathname, studentGroupAccordionService.isFloated])

  const checkVisibleLogo = () => {
    // 수업 준비탭은 항상 로고 표시
    if (isLessonPreparation) {
      setVisibleLogo(true)
      return
    }

    const documentOffset = document.body.offsetWidth

    // 가로 1280px 이하 + 폴딩이 접혀있을때 + 좌측 학생리스트가 있을 때 로고 제거
    if (documentOffset >= 1280) {
      setVisibleLogo(true)
      return
    }

    const isLessonPath = !!matchPath(location.pathname, { path: RouteName.lesson })
    const isAcademyPath = !!matchPath(location.pathname, { path: RouteName.academy })
    setVisibleLogo(!isLessonPath && !isAcademyPath)
  }

  window.addEventListener('resize', () => {
    checkVisibleLogo()
  })

  // 와이즈만은 공통 로고를 사용
  // customerType 값은 크레버스 권한 작업을 통해 사라질 예정이지만
  // 와이즈만 작업에서 이를 반영할 리소스가 부족하기에, 우선 B2B-프랜차이즈 값을 사용
  const logoUrl = commonRepo.academy?.isFranchise
    ? s3URL.mathflatUserUploadsBucket('logo/static/WEIZMANN_LOGO.svg')
    : commonRepo.academy?.logoUrlAdjusted

  return (
    <button
      className={clsx(
        (isLessonPreparation || (isVisibleLogo && !studentGroupAccordionService.isFloated)) &&
          'shown',
        className,
      )}
      css={logoStyle}
      onClick={handleClickLogo}
    >
      <img
        src={s3URL.mathflatAssetsBucket.scienceflat('scienceflat_logo.svg')}
        alt={commonRepo.academy?.name}
      />
    </button>
  )
}

export default observer(AcademyLogo)
